// shared postcode/suburb search input, used in two contexts told apart by the
// input's `data-admin` attribute:
//   - public "Find Your Daisy" search  -> initPublicSuburbSearch(): enter a postcode,
//     hit "Find Daisy", jump to the matching franchise (or head office).
//   - admin Franchisee edit form        -> initAdminSuburbSearch(): type a postcode,
//     pick unallocated suburbs to allocate to the franchisee via hidden form inputs.

function getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
}

// shared across bundles the same way window.Cursor/window.Money etc. are in admin.js —
// module.exports triggers an ESM/CommonJS interop error under this project's webpack config
window.getCsrfToken = getCsrfToken;

let token = "";

document.addEventListener("DOMContentLoaded", () => {
    token = getCsrfToken();
    document.querySelectorAll('.suburb-search-field').forEach(input => {
        input.hasAttribute('data-admin')
            ? initAdminSuburbSearch(input)
            : initPublicSuburbSearch(input);
    });
});



// ---------------------------------------------------------------------------
// Public: postcode -> franchise.
// One request to /suburbs-by-postcode returns the suburbs in that postcode, each
// already resolved to its destination URL. A single suburb navigates straight
// through; multiple suburbs (the postcode spans a mix of areas) show a picker.
// ---------------------------------------------------------------------------
function initPublicSuburbSearch(inputEl) {
    const widget = inputEl.closest('.suburb-search-widget');
    const listOfSuburbs = widget?.querySelector('.suburb-list');
    const button = widget?.querySelector('.suburb-search-submit');
    const message = widget?.parentElement?.querySelector('.suburb-search-message');

    async function handleSearch() {
        const postcode = inputEl.value.trim();
        hideMessage();
        clearList();
        if (!postcode) return;

        let suburbs;
        try {
            suburbs = await fetchSuburbsByPostcode(postcode);
        } catch (err) {
            console.error('Error fetching suburbs:', err);
            showMessage('Something went wrong. Please try again.');
            return;
        }

        if (!suburbs.length) {
            showMessage("Sorry, we couldn't find that postcode. Please check it and try again.");
            return;
        }

        if (suburbs.length === 1) {
            goTo(suburbs[0].url);
            return;
        }

        renderSuburbChoices(suburbs);
    }

    function renderSuburbChoices(suburbs) {
        if (!listOfSuburbs) return;
        suburbs.forEach(suburb => {
            const option = document.createElement('li');
            option.classList.add('suburb-list-item');
            option.textContent = suburb.suburb;
            // mousedown (not click) so it fires before the input's focus-out hides the list
            option.addEventListener('mousedown', () => goTo(suburb.url));
            listOfSuburbs.appendChild(option);
        });
        listOfSuburbs.classList.remove('d-none');
    }

    function goTo(url) {
        window.location.href = window.location.origin + url;
    }

    function clearList() {
        if (!listOfSuburbs) return;
        listOfSuburbs.replaceChildren();
        listOfSuburbs.classList.add('d-none');
    }

    function showMessage(text) {
        if (!message) return;
        message.textContent = text;
        message.classList.remove('d-none');
    }

    function hideMessage() {
        message?.classList.add('d-none');
    }

    button?.addEventListener('click', handleSearch);
    inputEl.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    });
    inputEl.addEventListener('input', hideMessage);
}

async function fetchSuburbsByPostcode(postcode) {
    const response = await fetch('/componentfranchisesearch/suburbs-by-postcode', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': token,
        },
        body: JSON.stringify({ _token: token, postcode })
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    return Array.isArray(data.result) ? data.result : [];
}



// ---------------------------------------------------------------------------
// Admin: Franchisee edit form. Loads the full suburb dataset (auth-gated /list)
// once, lets staff filter unallocated suburbs by typed postcode and batch-assign
// them. Selections/removals are written as hidden inputs the Franchisee form
// submits (handled server-side by PostController@update).
// ---------------------------------------------------------------------------
function initAdminSuburbSearch(inputEl) {
    let locationData = [];
    const batchAddSuburbs = [];
    const batchRemoveSuburbs = [];

    const widget = inputEl.closest('.suburb-search-widget');
    const currentFranchise = parseInt(inputEl.getAttribute('franchise_id'));
    const listOfSuburbs = widget?.querySelector('.suburb-list');
    const sububbles = widget?.querySelector('.suburb-allocations-list');

    inputEl.addEventListener('input', makeListElements);

    getFullSuburbList()
        .then(data => {
            locationData = data['result'];
            makeSuburbBubbles(getAllocatedSuburbs(locationData, currentFranchise));
            makeListElements();
        })
        .catch(err => console.error('Error fetching location data:', err));


    function makeListElements() {
        if (!listOfSuburbs) return;
        listOfSuburbs.replaceChildren();

        getSuburbsList(inputEl.value).forEach(e => {
            const option = document.createElement('li');
            option.classList.add('suburb-list-item');
            option.setAttribute('data-suburb', e.suburb);
            option.appendChild(document.createTextNode(e.suburb));
            option.setAttribute('postcode', e.postcode);
            option.setAttribute('id', e.id);
            option.addEventListener('mousedown', selectSuburb);
            listOfSuburbs.appendChild(option);
        });

        listOfSuburbs.classList.toggle('d-none', listOfSuburbs.children.length === 0);
    }

    // only unallocated suburbs in the typed postcode, minus any already queued to add
    function getSuburbsList(postcode) {
        let parsedSuburbs = locationData.filter(e =>
            e.postcode.toString().trim() === postcode.toString().trim()
            && e.franchisee_id == null
        );

        if (batchAddSuburbs.length > 0) {
            parsedSuburbs = parsedSuburbs.filter(ps =>
                !batchAddSuburbs.some(bs =>
                    ps.postcode === bs.postcode && ps.suburb === bs.suburb
                )
            );
        }

        return parsedSuburbs.sort((a, b) => {
            const suburbA = a.suburb.toUpperCase();
            const suburbB = b.suburb.toUpperCase();
            if (suburbA < suburbB) return -1;
            if (suburbA > suburbB) return 1;
            return 0;
        });
    }

    function getAllocatedSuburbs(locationArray, currentFranchiseId) {
        const ownedSuburbs = [];
        locationArray.forEach(loc => {
            if (loc.franchisee_id == currentFranchiseId) {
                ownedSuburbs.push({ id: loc.id, suburb: loc.suburb, postcode: loc.postcode });
            }
        });
        batchAddSuburbs.forEach(e => ownedSuburbs.push(e));
        return ownedSuburbs;
    }

    function makeSuburbBubbles(suburbList) {
        if (!sububbles) return;
        sububbles.replaceChildren();

        suburbList.forEach(e => {
            const bubble = document.createElement('li');
            bubble.classList.add('sububble', 'text-white', 'bg-primary', 'rounded-pill');
            bubble.setAttribute('postcode', e.postcode);
            bubble.setAttribute('suburb', e.suburb);
            bubble.setAttribute('id', e.id);

            bubble.appendChild(document.createTextNode(e.postcode + ' - ' + e.suburb));

            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('postcode', e.postcode);
            svg.setAttribute('suburb', e.suburb);
            svg.setAttribute('id', e.id);
            svg.addEventListener('click', startRemove);
            svg.setAttribute('viewBox', '0 0 640 640');
            svg.innerHTML = `<path d="M183.1 137.4C170.6 124.9 150.3 124.9 137.8 137.4C125.3 149.9 125.3 170.2 137.8 182.7L275.2 320L137.9 457.4C125.4 469.9 125.4 490.2 137.9 502.7C150.4 515.2 170.7 515.2 183.2 502.7L320.5 365.3L457.9 502.6C470.4 515.1 490.7 515.1 503.2 502.6C515.7 490.1 515.7 469.8 503.2 457.3L365.8 320L503.1 182.6C515.6 170.1 515.6 149.8 503.1 137.3C490.6 124.8 470.3 124.8 457.8 137.3L320.5 274.7L183.1 137.4z"/>`;
            svg.setAttribute('width', '23px');
            svg.classList.add('sububble-x');

            bubble.appendChild(svg);
            sububbles.appendChild(bubble);
        });

        if (sububbles.children.length > 0) {
            sububbles.classList.remove('d-none');
            const col = widget?.querySelector('.sububble-list-col');
            if (col) {
                const space = sububbles.children.length * sububbles.getBoundingClientRect().height;
                col.style.setProperty('padding-bottom', space / 2 + "px");
            }
        } else {
            sububbles.classList.add('d-none');
        }
    }

    // begin assignment of suburb to current franchisee
    function selectSuburb() {
        const values = { id: this.getAttribute('id'), postcode: this.getAttribute('postcode'), suburb: this.textContent };
        batchAddSuburbs.push(values);
        this.removeEventListener('click', selectSuburb);
        this.classList.add('text-green');
        this.textContent = 'Adding to database!';

        const selectionPane = widget?.querySelector('.suburb-selection-admin-pane');
        if (selectionPane) {
            const change = document.createElement('input');
            change.type = 'hidden';
            change.name = "suburbs[]";
            change.value = this.getAttribute('id');
            selectionPane.appendChild(change);
        }

        makeSuburbBubbles(getAllocatedSuburbs(locationData, currentFranchise));

        const index = batchRemoveSuburbs.findIndex(e => e.id === values.id);
        if (index !== -1) batchRemoveSuburbs.splice(index, 1);

        // remove from subtractions
        widget?.querySelectorAll('input[name="removedSuburbs[]"]').forEach(e => {
            if (e.value == values.id) e.remove();
        });
    }

    function startRemove() {
        const values = { id: this.getAttribute('id'), postcode: this.getAttribute('postcode'), suburb: this.textContent };
        batchRemoveSuburbs.push(values);

        const allocationsPane = widget?.querySelector('.suburb-allocations-admin-pane');
        if (allocationsPane) {
            const change = document.createElement('input');
            change.type = 'hidden';
            change.name = "removedSuburbs[]";
            change.value = this.getAttribute('id');
            allocationsPane.appendChild(change);
        }

        const index = batchAddSuburbs.findIndex(e => e.id === values.id);
        if (index !== -1) batchAddSuburbs.splice(index, 1);

        // remove from additions
        widget?.querySelectorAll('input[name="suburbs[]"]').forEach(e => {
            if (e.value == values.id) e.remove();
        });

        this.parentElement.remove();
    }
}

async function getFullSuburbList() {
    return await fetch('/componentfranchisesearch/list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ _token: getCsrfToken() })
    })
        .then(response => response.json())
        .catch(err => { console.error('Error:', err); });
}
